import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { createPortal } from 'react-dom';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LOADER_OPTIONS,
} from '../utils/googleMapsLoader';

// City/ZIP autocomplete for the Load Board request flow (THT-695). A lighter
// sibling of signup/customers/InputWithPlacesAPI: same Google Places setup (new
// AutocompleteSuggestion API, shared session token, portaled dropdown so a
// modal's overflow can't clip it), but driven by a plain value/onChange pair
// instead of react-hook-form, and narrowed to cities and ZIPs rather than
// street addresses.
//
// Free text is always allowed — the pickup string is read by a human
// dispatcher, so a typed "Dallas, TX" must still submit if Places is
// unavailable, blocked, or out of quota. Suggestions are an assist, never a
// gate. With no API key, or before the SDK loads, this degrades to a plain
// text input.

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 250;

export interface CityAutocompleteSelection {
  /** Human-readable pickup string, e.g. "Dallas, TX 75201". */
  display: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface CityAutocompleteProps {
  id?: string;
  value: string;
  /** Fires on every keystroke — free text is valid on its own. */
  onChange: (text: string) => void;
  /** Fires only when a suggestion is picked, with the parsed parts. */
  onSelect: (selection: CityAutocompleteSelection) => void;
  placeholder?: string;
  addClass?: string;
  testId?: string;
}

interface PlaceComponent {
  longText?: string;
  shortText?: string;
  types?: string[];
}

const pick = (
  components: PlaceComponent[],
  type: string,
  short = false
): string | undefined => {
  const match = components.find((c) => c.types?.includes(type));
  if (!match) return undefined;
  return (short ? match.shortText : match.longText) ?? undefined;
};

const CityAutocomplete = ({
  id,
  value,
  onChange,
  onSelect,
  placeholder,
  addClass,
  testId,
}: CityAutocompleteProps) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef = useRef<any>(null);
  // Only a real keystroke may open the dropdown.
  //
  // `value` is controlled by the parent, so it also changes for reasons the
  // user did not initiate: RequestLoadModal writes the reverse-geocoded city
  // into it when "Use current location" succeeds, and returning from the
  // Review step remounts this input with the field already filled. Fetching on
  // those would portal a `z-[10001]` listbox over the modal that the user
  // never asked for — and because the input was never focused, its
  // blur-to-close can never fire, so the panel just sits there swallowing
  // clicks on whatever is underneath.
  //
  // This also replaces the old `skipNextFetchRef` (set on selection, consumed
  // by the next effect run): when a picked suggestion's display text equalled
  // what was already typed, `value` didn't change, the effect never ran, and
  // the stale flag went on to swallow the user's NEXT genuine lookup. Keying
  // off "did the user type?" has no such carry-over — selecting simply clears
  // it, and the next keystroke sets it again.
  const userTypedRef = useRef(false);

  // Generation counter for the debounced Places lookup, so `close()` can
  // abandon work that is already in flight.
  //
  // The effect's local `cancelled` flag is only tripped by the effect's own
  // cleanup — i.e. by a `value` change or unmount — which left `close()`
  // (blur, Escape, selection) unable to stop anything. Blur within the ~250ms
  // debounce window meant the fetch still resolved and re-opened the
  // `z-[10001]` portaled listbox over the modal with the input NOT focused, so
  // blur-to-close could never fire again and there is no outside-click
  // handler: the panel just sat there until the user re-focused the field, or
  // mousedowned a row and silently changed the form value. Bumping this
  // counter invalidates any pending timer AND any resolved-but-unapplied
  // response, which is the same stuck-panel class the `userTypedRef` rework
  // was meant to retire.
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ties the input to its portaled listbox for screen readers — the list is
  // rendered on document.body, so the DOM nesting can't imply the relationship.
  const listboxId = `${id ?? 'city-autocomplete'}-listbox`;

  const close = useCallback(() => {
    requestIdRef.current += 1;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setSuggestions([]);
    setActiveIndex(-1);
    // Closing ends the current editing burst: nothing may reopen the list
    // until the user types again.
    userTypedRef.current = false;
  }, []);

  // Debounced suggestion fetch. A fresh session token per burst of typing is
  // what makes Places bill the lookup as one session instead of per keystroke.
  useEffect(() => {
    if (!isLoaded || !GOOGLE_MAPS_API_KEY) return undefined;
    if (!userTypedRef.current) return undefined;

    const query = value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      close();
      return undefined;
    }

    // Claim this generation; anything that bumps the counter afterwards
    // (close(), or this effect re-running) makes the work below a no-op.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;

    const timer = setTimeout(async () => {
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } = (await (
          window.google.maps as any
        ).importLibrary('places')) as any;

        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new AutocompleteSessionToken();
        }

        const { suggestions: results } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            sessionToken: sessionTokenRef.current,
            language: 'en-US',
            region: 'us',
            includedRegionCodes: ['us'],
            // Cities and ZIPs only — this field is a pickup area, not a street
            // address, so street-level results would be noise.
            includedPrimaryTypes: [
              'locality',
              'administrative_area_level_3',
              'postal_code',
            ],
          });

        if (!isCurrent()) return;
        setSuggestions(results ?? []);
        setActiveIndex(-1);
      } catch {
        // Places failing is not an error state here — free text still submits.
        if (isCurrent()) close();
      }
    }, DEBOUNCE_MS);
    debounceTimerRef.current = timer;

    return () => {
      requestIdRef.current += 1;
      clearTimeout(timer);
      if (debounceTimerRef.current === timer) debounceTimerRef.current = null;
    };
  }, [value, isLoaded, close]);

  // Portaled dropdown: track the input's viewport position so it stays glued
  // while the surrounding modal scrolls.
  useLayoutEffect(() => {
    if (!suggestions.length) return undefined;

    const update = () => {
      if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [suggestions]);

  const handleSelect = useCallback(
    async (suggestion: any) => {
      try {
        const place = suggestion.placePrediction.toPlace();
        await place.fetchFields({
          fields: ['formattedAddress', 'addressComponents'],
        });

        const components: PlaceComponent[] = place.addressComponents ?? [];
        const city =
          pick(components, 'locality') ??
          pick(components, 'postal_town') ??
          pick(components, 'sublocality');
        const state = pick(components, 'administrative_area_level_1', true);
        const zip = pick(components, 'postal_code');

        const parts = [city, state].filter(Boolean).join(', ');
        const display =
          [parts, zip].filter(Boolean).join(' ') ||
          place.formattedAddress ||
          suggestion.placePrediction.text?.toString() ||
          '';

        userTypedRef.current = false;
        onSelect({ display, city, state, zip });
      } catch {
        // Details lookup failed — fall back to the suggestion's own label so
        // the pick still does something useful.
        const text = suggestion.placePrediction?.text?.toString() ?? '';
        if (text) {
          userTypedRef.current = false;
          onSelect({ display: text });
        }
      } finally {
        // One session token covers one search-through-selection cycle.
        sessionTokenRef.current = null;
        close();
      }
    },
    [onSelect, close]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      // Only intercept Enter when a suggestion is actively highlighted, so
      // plain typing + Enter still submits the free-text value.
      event.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      close();
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          userTypedRef.current = true;
          onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        // Blur is delayed so a mousedown on a suggestion lands first.
        onBlur={() => setTimeout(close, 150)}
        placeholder={placeholder}
        className={addClass}
        data-testid={testId}
        autoComplete="off"
        role="combobox"
        aria-expanded={suggestions.length > 0}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
        }
      />

      {suggestions.length > 0 &&
        rect &&
        createPortal(
          <ul
            id={listboxId}
            role="listbox"
            data-testid={testId ? `${testId}-suggestions` : undefined}
            className="fixed z-[10001] max-h-60 overflow-y-auto rounded-lg border border-bordergray bg-white py-1 shadow-lg"
            style={{
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width,
            }}
          >
            {suggestions.map((suggestion, index) => {
              const label = suggestion.placePrediction?.text?.toString() ?? '';
              return (
                <li
                  key={label || index}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                >
                  <button
                    type="button"
                    // mousedown beats the input's blur; click would be too late.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(suggestion);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={`block w-full px-3 py-2 text-left text-sm text-gray-700 ${
                      index === activeIndex ? 'bg-bgyellow' : 'bg-white'
                    }`}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default CityAutocomplete;
